/**
 * TotalAccessOfflineDB — single IndexedDB for offline domains (encrypted payloads per store).
 *
 * `app-shell`: Smart Task / operations KV (list caches, mutation ledger wrappers). Diagnostic module breadcrumbs now persist here via `lastVisitedModuleStore.ts`.
 *
 * Connect email UI thread/folder cache lives on this DB (`emails` domain stores `connect_cache_*`) — see `emailUiCacheStore.ts`. Email UI preferences use opaque `emails` meta via `emailConnectPreferencesStore.ts`; offline list-cache retention days use opaque meta segment `cache_retention_days`. Last-known work-mailbox access for Total Connect is stored as opaque `emails` meta (`workEmailAccessCache`). Legacy `totalaccess_email_local_cache` is migrated away on first init.
 *
 * Messaging UI caches threads/messages on the `messages` domain (`cached_threads`, etc.) — see `messagesStore.ts`. Last-known Operations feature flags for Message Centre are stored as `messages` meta (`operations_my_features`, see `messagingFeatureSettingsCache.ts`).
 *
 * Offline auth credentials (`offline-auth` domain) migrated from legacy `TotalAccessOfflineAuthDB` — see `authOfflineStore.ts`.
 */
import Dexie from 'dexie';
import type { Table } from 'dexie';

export type OfflineDomain =
  | 'workitems'
  | 'attendance'
  | 'messages'
  | 'emails'
  | 'app-shell'
  | 'offline-auth';
export type OfflineRecordSyncStatus = 'synced' | 'pending' | 'failed' | 'quarantined';
export type OfflineOutboxStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type PublishRetryStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'dead_letter';

export interface OfflineDomainRecord {
  id?: number;
  domain: OfflineDomain;
  store: string;
  client_id: number;
  entity_id: string;
  parent_id?: string;
  status?: string;
  sync_status: OfflineRecordSyncStatus;
  created_at: string;
  updated_at: string;
  payload: string;
  payload_size: number;
}

export interface OfflineOutboxRecord {
  id?: number;
  domain: OfflineDomain;
  client_id: number;
  operation: string;
  entity_type: string;
  entity_id: string;
  status: OfflineOutboxStatus;
  priority: number;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
  last_attempt?: string;
  next_retry_at?: string;
  error_message?: string;
  payload: string;
}

export interface OfflineMetaRecord {
  key: string;
  domain: OfflineDomain;
  client_id: number;
  updated_at: string;
  payload: string;
}

export interface PublishRetryRecord {
  id?: number;
  domain: OfflineDomain;
  client_id: number;
  operation: string;
  entity_type: string;
  entity_id: string;
  status: PublishRetryStatus;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
  last_attempt?: string;
  next_retry_at?: string;
  error_message?: string;
  payload: string;
  response_payload?: string;
}

/** Persisted badge counts — survives app restarts so badges show instantly on cold start. */
export interface BadgeCacheRecord {
  key: string;
  counts: Record<string, number | string>;
  updated_at: string;
}

class TotalAccessOfflineDatabase extends Dexie {
  records!: Table<OfflineDomainRecord, number>;
  outbox!: Table<OfflineOutboxRecord, number>;
  meta!: Table<OfflineMetaRecord, string>;
  publishQueue!: Table<PublishRetryRecord, number>;
  badges!: Table<BadgeCacheRecord, string>;

  constructor() {
    super('TotalAccessOfflineDB');

    this.version(1).stores({
      records: '++id, [domain+client_id], [domain+store+client_id], [domain+store+client_id+entity_id], [domain+store+client_id+parent_id], [domain+store+client_id+status], [domain+store+client_id+sync_status], entity_id, updated_at',
      outbox: '++id, [domain+client_id], [domain+client_id+status], [domain+client_id+entity_type+entity_id], [domain+client_id+priority], [domain+client_id+operation], created_at, next_retry_at',
      meta: 'key, [domain+client_id], updated_at',
    });

    this.version(2).stores({
      records: '++id, [domain+client_id], [domain+store+client_id], [domain+store+client_id+entity_id], [domain+store+client_id+parent_id], [domain+store+client_id+status], [domain+store+client_id+sync_status], entity_id, updated_at',
      outbox: '++id, [domain+client_id], [domain+client_id+status], [domain+client_id+entity_type+entity_id], [domain+client_id+priority], [domain+client_id+operation], created_at, next_retry_at',
      meta: 'key, [domain+client_id], updated_at',
      publishQueue: '++id, [domain+client_id], [domain+client_id+status], [domain+client_id+entity_type+entity_id], [domain+client_id+operation], next_retry_at, created_at',
    });

    this.version(3).stores({
      records: '++id, [domain+client_id], [domain+store+client_id], [domain+store+client_id+entity_id], [domain+store+client_id+parent_id], [domain+store+client_id+status], [domain+store+client_id+sync_status], entity_id, updated_at',
      outbox: '++id, [domain+client_id], [domain+client_id+status], [domain+client_id+entity_type+entity_id], [domain+client_id+priority], [domain+client_id+operation], created_at, next_retry_at',
      meta: 'key, [domain+client_id], updated_at',
      publishQueue: '++id, [domain+client_id], [domain+client_id+status], [domain+client_id+entity_type+entity_id], [domain+client_id+operation], next_retry_at, created_at',
      badges: 'key, updated_at',
    });
  }
}

const offlineDb = typeof window !== 'undefined' ? new TotalAccessOfflineDatabase() : null;

export default offlineDb;
export { TotalAccessOfflineDatabase };